import React from 'react';
import { Icon as CrateIcon } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import { RegionReportFooter } from '../RegionReportFooter/RegionReportFooter';
import { SILAGE_CORN_CROP_CODE } from '../../../../constants/cropCodes';
import { RegionReportStats } from '../RegionReportStats/RegionReportStats';
import { YieldRegionReport } from '../../../../types';
import styles from '../RegionReport.css';
import { RegionReportSummary } from '../RegionReportSummary/RegionReportSummary';
import { getAverageYieldText } from '../../../../utils/regionUtils'

export type RegionReportDetailProps = {
  cropCode: string;
  isAirSeeder: boolean;
  itemData: YieldRegionReport;
  onCancelClick: () => void;
  onClick: () => void;
  onDeleteClick: () => void;
  saveClick: () => void;
  showSaveButton: boolean;
  index: number;
};

export const RegionReportDetail = ({
  cropCode,
  index,
  isAirSeeder,
  itemData,
  onCancelClick,
  onDeleteClick,
  onClick,
  saveClick,
  showSaveButton,
}: RegionReportDetailProps) => {

  const isSilageCrop = cropCode === SILAGE_CORN_CROP_CODE;

  const {
    averageYield,
    totalWeight,
    area,
    averageMoisture,
    hybridStats = [],
    soilStats = [],
    populationStats = [],
    elevationStats = [],
    planterDateStats = [],
    seedTreatmentStats = [],
  } = itemData;

  const averageYieldText = getMessage(getAverageYieldText(cropCode));
  const areaUnit =
    area.u === 'ac'
      ? getMessage('regions.report.modal.acre')
      : getMessage('regions.report.modal.hectare');
  return (
    <div>
      <h2 className={styles.title}>
        <CrateIcon
          className={styles.buttonBack}
          glyph={CrateIcon.set.ARROW_BACK}
          onClick={onClick}
          testId='frr-go-back-button'
        />
        {itemData.label}
      </h2>
      <CrateIcon
        className={styles.closeModalTitle}
        glyph={CrateIcon.set.REMOVE}
        onClick={onCancelClick}
        testId='region-report-detail-close-icon'
      />
      <RegionReportSummary
        averageYield={averageYield}
        averageYieldText={averageYieldText}
        area={area}
        totalWeight={totalWeight}
        withDryMatterYield={isSilageCrop}
        cropCode={cropCode}
        averageMoisture={averageMoisture}
      />
      {hybridStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={hybridStats}
          dataTestId="hybrids"
          headerText={getMessage('regions.report.modal.byHybrid')}
          index={index}
        />
      )}
      {soilStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={soilStats}
          dataTestId="soil"
          headerText={getMessage('regions.report.modal.bySoil')}
          index={index}
        />
      )}
      {!isAirSeeder && populationStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={populationStats}
          dataTestId="population"
          headerText={getMessage('regions.report.modal.byPopulation')}
          index={index}
        />
      )}
      {elevationStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={elevationStats}
          dataTestId="elevation"
          headerText={getMessage('regions.report.modal.byElevation')}
          index={index}
        />
      )}
      {planterDateStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={planterDateStats}
          dataTestId="planterDate"
          headerText={getMessage('regions.report.modal.byPlanterDate')}
          index={index}
        />
      )}
      {seedTreatmentStats.length > 0 && (
        <RegionReportStats
          allowHover={false}
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={seedTreatmentStats}
          dataTestId="seedTreatment"
          headerText={getMessage('regions.report.modal.bySeedTreatment')}
          index={index}
        />
      )}
      <RegionReportFooter
        onCancelClick={onCancelClick}
        onDeleteClick={onDeleteClick}
        saveClick={saveClick}
        showSaveButton={showSaveButton}
      />
    </div>
  );
};
